import moment from 'moment';

export type EmployeeListStatusType =
  | 'online'
  | 'idle'
  | 'offline'
  | 'absent'
  | 'invited'
  | 'auto-logout'
  | 'none';

export type EmployeeStatusSource = {
  status?: unknown;
  meta?: {
    ss_password_reset_at?: string | null;
    ss_password_reset_by?: number | string | null;
    ss_password_reset_by_name?: string | null;
    ss_password_last_reset_at?: string | null;
    ss_password_last_reset_by?: number | string | null;
    ss_password_last_reset_by_name?: string | null;
  } | null;
} | null | undefined;

/**
 * Classify an employee the same way the Employees table Status column does.
 *
 * Named values (`online`, `idle`, `auto-logout`, `not-using`) are stored as
 * strings. Offline/absent users keep their last-active timestamp in `status`,
 * which is also a string (or occasionally a numeric unix time).
 */
export function getEmployeeListStatusType(
  user: EmployeeStatusSource,
  getDayDifference: (lastOnlineAt: unknown) => number
): EmployeeListStatusType {
  if (!user) {
    return 'none';
  }

  const status = user.status;
  if (status === 'auto-logout') {
    return 'auto-logout';
  }
  if (status === 'idle') {
    return 'idle';
  }
  if (status === 'online') {
    return 'online';
  }
  if (!status || status === 'not-using') {
    return 'invited';
  }

  return getDayDifference(status) > 0 ? 'absent' : 'offline';
}

export function filterEmployeesBySelectedStatuses<T extends EmployeeStatusSource>(
  users: T[] | null | undefined,
  selectedStatuses: string[] | null | undefined,
  getDayDifference: (lastOnlineAt: unknown) => number
): T[] {
  if (!users?.length) {
    return [];
  }
  if (
    !selectedStatuses?.length ||
    (selectedStatuses.length === 1 && selectedStatuses[0] === 'all')
  ) {
    return users.slice();
  }

  const selected = new Set(selectedStatuses);
  const matchActive = selected.has('active');

  return users.filter((user) => {
    if (!user) {
      return false;
    }
    const statusType = getEmployeeListStatusType(user, getDayDifference);
    if (matchActive && (statusType === 'online' || statusType === 'idle')) {
      return true;
    }
    return selected.has(statusType);
  });
}

function metaString(value: unknown): string {
  if (typeof value !== 'string') {
    return '';
  }
  return value.trim();
}

/** UTC MySQL datetime from `meta.ss_password_reset_at`, or empty when unset. */
export function getPasswordResetAt(user: EmployeeStatusSource): string {
  return metaString(user?.meta?.ss_password_reset_at);
}

/** Display name of the admin who first signed them out, if known. */
export function getPasswordResetByName(user: EmployeeStatusSource): string {
  return metaString(user?.meta?.ss_password_reset_by_name);
}

export function getLastPasswordResetAt(user: EmployeeStatusSource): string {
  return metaString(user?.meta?.ss_password_last_reset_at);
}

export function getLastPasswordResetByName(user: EmployeeStatusSource): string {
  return metaString(user?.meta?.ss_password_last_reset_by_name);
}

function formatStampLabel(
  prefix: string,
  utcMysqlDatetime: string,
  byName = ''
): string {
  if (!utcMysqlDatetime || !String(utcMysqlDatetime).trim()) {
    return '';
  }
  const parsed = moment.utc(String(utcMysqlDatetime).trim(), 'YYYY-MM-DD HH:mm:ss', true);
  if (!parsed.isValid()) {
    return '';
  }
  const when = parsed.local().format('D MMM YYYY, h:mm A');
  const who = byName && byName.trim() ? ` by ${byName.trim()}` : '';
  return `${prefix} ${when}${who}`;
}

/**
 * Customer-facing label for when the employee was signed out by a password reset.
 * Returns empty string if the timestamp is missing or invalid.
 */
export function formatSignedOutLabel(utcMysqlDatetime: string, byName = ''): string {
  return formatStampLabel('Signed out', utcMysqlDatetime, byName);
}

export function formatPasswordResetLabel(utcMysqlDatetime: string, byName = ''): string {
  return formatStampLabel('Password reset', utcMysqlDatetime, byName);
}

export type PasswordResetHistoryEventType = 'password_reset' | 'signed_in';

export type PasswordResetHistoryRow = {
  kind: PasswordResetHistoryEventType;
  label: string;
  when: string;
  who: string;
};

/** One history-dialog row: kind + local datetime + actor, or empty when invalid. */
export function formatPasswordResetHistoryRow(
  utcMysqlDatetime: string,
  byName = '',
  type: PasswordResetHistoryEventType | string = 'password_reset'
): PasswordResetHistoryRow {
  const kind: PasswordResetHistoryEventType = type === 'signed_in' ? 'signed_in' : 'password_reset';
  const when = formatStampLabel('', utcMysqlDatetime, '').trim();
  if (!when) {
    return { kind, label: '', when: '', who: '' };
  }
  return {
    kind,
    label: kind === 'signed_in' ? 'Signed in' : 'Password reset',
    when,
    who: kind === 'signed_in' ? '' : (byName && byName.trim() ? byName.trim() : ''),
  };
}

/** Show Signed out line only for offline/absent when a reset timestamp exists. */
export function shouldShowSignedOut(
  user: EmployeeStatusSource,
  statusType: EmployeeListStatusType | string
): boolean {
  if (statusType !== 'offline' && statusType !== 'absent') {
    return false;
  }
  return !!getPasswordResetAt(user);
}

/** Convenience: label to render, or empty when it should be hidden. */
export function getSignedOutLabelForUser(
  user: EmployeeStatusSource,
  statusType: EmployeeListStatusType | string
): string {
  if (!shouldShowSignedOut(user, statusType)) {
    return '';
  }
  return formatSignedOutLabel(getPasswordResetAt(user), getPasswordResetByName(user));
}

/** Extra line when a later reset happened while they were still signed out. */
export function getPasswordResetLabelForUser(
  user: EmployeeStatusSource,
  statusType: EmployeeListStatusType | string
): string {
  if (statusType !== 'offline' && statusType !== 'absent') {
    return '';
  }
  const firstAt = getPasswordResetAt(user);
  const lastAt = getLastPasswordResetAt(user);
  if (!lastAt || lastAt === firstAt) {
    return '';
  }
  return formatPasswordResetLabel(lastAt, getLastPasswordResetByName(user));
}
